import { Calendar as CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { isIOS } from "@/lib/platform";
import { timezoneUtils } from "@/lib/timezone";
import { cn } from "@/lib/utils";

interface HorizontalDayListProps {
  currentDate: DateTime;
  onDateSelect: (date: DateTime) => void;
  className?: string;
  isMobile?: boolean;
  forceCenter?: boolean; // Force centering even if user has scrolled
}

export function HorizontalDayList({
  currentDate,
  onDateSelect,
  className,
  isMobile = false,
  forceCenter = false,
}: HorizontalDayListProps) {
  const { i18n } = useTranslation("calendar");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [animatingDate, setAnimatingDate] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [_userScrolled, setUserScrolled] = useState(false);
  const [shouldAutoCenter, setShouldAutoCenter] = useState(true);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Generate stable date range that doesn't change when currentDate changes
  const totalDays = isMobile ? 11 : 50; // Mobile: 11 days, Desktop: 50 days
  const startOffset = -3; // Start 3 days ago

  // Create stable date range based on initial mount or when we need to expand
  const [baseDateRange, setBaseDateRange] = useState(() => {
    const today = DateTime.now();
    return Array.from({ length: totalDays }, (_, index) => {
      const offset = startOffset + index;
      return today.plus({ days: offset });
    });
  });

  // Recalculate date range when isMobile changes
  useEffect(() => {
    const today = DateTime.now();
    const newRange = Array.from({ length: totalDays }, (_, index) => {
      const offset = startOffset + index;
      return today.plus({ days: offset });
    });
    setBaseDateRange(newRange);
  }, [totalDays]);

  // Check if current date needs range expansion
  useEffect(() => {
    const currentDateStr = currentDate.toISODate();
    const index = baseDateRange.findIndex((date) => date.toISODate() === currentDateStr);

    // If current date is not in range, expand the range efficiently
    if (index === -1) {
      const firstDate = baseDateRange[0];
      const lastDate = baseDateRange[baseDateRange.length - 1];

      // Determine if we need to expand backwards or forwards
      if (currentDate < firstDate) {
        // Expand backwards - create new range starting from currentDate + startOffset
        const newRange = Array.from({ length: totalDays }, (_, i) => {
          const offset = startOffset + i;
          return currentDate.plus({ days: offset });
        });
        setBaseDateRange(newRange);
      } else if (currentDate > lastDate) {
        // Expand forwards - create new range starting from currentDate + startOffset
        const newRange = Array.from({ length: totalDays }, (_, i) => {
          const offset = startOffset + i;
          return currentDate.plus({ days: offset });
        });
        setBaseDateRange(newRange);
      }
    }
  }, [currentDate, baseDateRange, totalDays]);

  // Find current date index in stable range
  const currentDateIndex = useMemo(() => {
    const currentDateStr = currentDate.toISODate();
    const index = baseDateRange.findIndex((date) => date.toISODate() === currentDateStr);
    return index !== -1 ? index : 3; // Default to index 3 (today's position when starting 3 days ago)
  }, [currentDate, baseDateRange]);

  const dates = baseDateRange;

  // Auto-scroll to center the current date when it changes (only if should auto-center or forced)
  useEffect(() => {
    if (!shouldAutoCenter && !forceCenter) {
      return;
    }

    // Reset user scroll state when force centering
    if (forceCenter) {
      setUserScrolled(false);
      setShouldAutoCenter(true);
    }

    setIsTransitioning(true);

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;

      // Find the current date element
      const allButtons = container.querySelectorAll("button");
      const centerElement = allButtons[currentDateIndex];

      if (centerElement) {
        const containerWidth = container.offsetWidth;
        const elementWidth = centerElement.offsetWidth;
        const elementLeft = centerElement.offsetLeft;

        const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2;

        // Immediate scroll without animation
        container.scrollLeft = scrollPosition;

        // End transition after scroll completes
        setTimeout(() => setIsTransitioning(false), 300);
      } else {
        setTimeout(() => setIsTransitioning(false), 100);
      }
    } else {
      setTimeout(() => setIsTransitioning(false), 100);
    }
  }, [currentDateIndex, shouldAutoCenter, forceCenter]);

  // Initial scroll to center on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const allButtons = container.querySelectorAll("button");
      const centerElement = allButtons[currentDateIndex];

      if (centerElement) {
        const containerWidth = container.offsetWidth;
        const elementWidth = centerElement.offsetWidth;
        const elementLeft = centerElement.offsetLeft;

        const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2;

        // Initial scroll without animation - immediate positioning
        container.scrollLeft = scrollPosition;
      }
    }
  }, [currentDateIndex]); // Run on mount and when currentDateIndex changes

  const formatDayDisplay = (date: DateTime, isMobile: boolean) => {
    if (isMobile) {
      return {
        dayName: date.setLocale(i18n.language).toFormat("ccc"), // Mon, Tue, etc. (localized)
        dayNumber: date.toFormat("d"), // 1, 2, etc.
      };
    }
    return {
      dayName: date.setLocale(i18n.language).toFormat("ccc"), // Mon, Tue, etc. (localized)
      dayNumber: date.toFormat("d"), // 1, 2, etc.
    };
  };

  const isToday = (date: DateTime) => {
    const today = DateTime.now();
    return date.hasSame(today, "day");
  };

  const isCurrentDate = (date: DateTime) => {
    return date.hasSame(currentDate, "day");
  };

  const handleDateSelect = (date: DateTime) => {
    const dateKey = date.toISODate() ?? "";
    setAnimatingDate(dateKey);

    // Don't auto-center when user selects from the horizontal list
    setShouldAutoCenter(false);

    // Trigger animation and call parent handler
    setTimeout(() => {
      onDateSelect(date);
      setAnimatingDate(null);
      // Re-enable auto-centering after a delay to allow for external date changes
      setTimeout(() => setShouldAutoCenter(true), 1000);
    }, 150); // Short delay for visual feedback
  };

  const handleDateJump = (date: Date | undefined) => {
    if (!date) return;
    const newDate = timezoneUtils.fromJSDate(date);
    if (newDate.isValid) {
      onDateSelect(newDate);
      setShouldAutoCenter(true);
      setUserScrolled(false);
      setDatePickerOpen(false);
    }
  };

  // On iOS keep the native date input so users get the familiar OS date wheel;
  // web/android use the calendar popover below.
  const handleDateJumpNative = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = event.target.value;
    if (!selectedDate) return;
    const newDate = timezoneUtils.fromISO(selectedDate);
    if (newDate.isValid) {
      onDateSelect(newDate);
      setShouldAutoCenter(true);
      setUserScrolled(false);
    }
  };

  // Handle manual scroll detection
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      setUserScrolled(true);
      setShouldAutoCenter(false);

      // Clear existing timeout
      clearTimeout(scrollTimeout);

      // Re-enable auto-centering after user stops scrolling for 2 seconds
      scrollTimeout = setTimeout(() => {
        setShouldAutoCenter(true);
        setUserScrolled(false);
      }, 2000);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  return (
    <div
      className={cn("bg-background rounded-lg border p-2", "flex items-center gap-2", className)}
    >
      <div
        ref={scrollContainerRef}
        className={cn(
          "flex gap-1 overflow-x-auto overflow-y-visible scrollbar-hide relative min-w-0 flex-1",
          "transition-all duration-300 ease-out",
          isMobile ? "px-1 py-0" : "px-0 py-0",
          isTransitioning && "opacity-90"
        )}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {dates.map((date, _index) => {
          const { dayName, dayNumber } = formatDayDisplay(date, isMobile);
          const today = isToday(date);
          const current = isCurrentDate(date);
          const dateKey = date.toISODate() ?? "";
          const isAnimating = animatingDate === dateKey;

          return (
            <button
              type="button"
              key={dateKey}
              data-is-current={current}
              onClick={() => handleDateSelect(date)}
              className={cn(
                "flex flex-col items-center justify-center rounded-md flex-shrink-0 relative overflow-hidden",
                "transition-all duration-200 ease-out",
                isMobile ? "px-2 py-1 min-w-[40px] h-12" : "px-2 py-1.5 min-w-[36px] h-10",
                current
                  ? "bg-primary text-primary-foreground shadow-sm font-semibold"
                  : today
                    ? "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 font-semibold shadow-sm border border-orange-200 dark:border-orange-800"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent hover:scale-105",
                isAnimating && "scale-105 bg-primary/10"
              )}
            >
              <span
                className={cn(
                  "font-medium leading-none",
                  isMobile ? "text-xs mb-0.5" : "text-xs mb-0"
                )}
              >
                {dayName}
              </span>
              <span className={cn("font-semibold leading-none", isMobile ? "text-xs" : "text-xs")}>
                {dayNumber}
              </span>

              {/* Ripple effect overlay */}
              {isAnimating && (
                <div className="absolute inset-0 bg-primary/20 rounded-md animate-ping" />
              )}
            </button>
          );
        })}
      </div>

      {/* Vertical Divider */}
      <div className="w-px bg-border h-8 flex-shrink-0" />

      {/* Jump to Date Button — native OS date wheel on iOS, calendar popover elsewhere */}
      {isIOS() ? (
        <div className="relative flex-shrink-0">
          <label
            className={cn(
              "flex items-center justify-center rounded-md cursor-pointer",
              "transition-all duration-200 ease-out",
              "text-muted-foreground hover:text-foreground hover:bg-accent",
              isMobile ? "w-12 h-12" : "w-10 h-10"
            )}
          >
            <CalendarIcon className={cn(isMobile ? "h-5 w-5" : "h-4 w-4")} />
            <input
              type="date"
              value={currentDate.toISODate() || ""}
              onChange={handleDateJumpNative}
              className="absolute inset-0 opacity-0 cursor-pointer"
              title="Jump to date"
            />
          </label>
        </div>
      ) : (
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Jump to date"
              className={cn(
                "flex items-center justify-center rounded-md flex-shrink-0",
                "transition-all duration-200 ease-out",
                "text-muted-foreground hover:text-foreground hover:bg-accent",
                isMobile ? "w-12 h-12" : "w-10 h-10"
              )}
            >
              <CalendarIcon className={cn(isMobile ? "h-5 w-5" : "h-4 w-4")} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            collisionPadding={12}
            className="w-[min(260px,calc(100vw-1.5rem))] p-0"
          >
            <CalendarPicker
              mode="single"
              selected={currentDate.toJSDate()}
              onSelect={handleDateJump}
              initialFocus
              className="p-2 [--cell-size:1.75rem] text-xs"
              modifiers={{ weekend: (day) => day.getDay() === 0 || day.getDay() === 6 }}
              modifiersClassNames={{
                weekend:
                  "text-red-500 data-[selected-single=true]:text-primary-foreground [&_button]:text-red-500 [&_button[data-selected-single=true]]:text-primary-foreground",
              }}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
